/*
  Zen Gift Guard
  ==============
  Single source of truth for the rule "one free-gift set per qualifying offer".

  WHY THIS EXISTS (Aug 2026)
  Free gifts were reaching checkout at quantity 2+ (Jose's report + the order
  screenshots). Three independent paths allowed it:

    1. /cart rendered a bare <input type="number"> on gift lines, so a customer
       could type any quantity and press "Update cart".
    2. /cart/add.js MERGES an add into an existing line when variant +
       properties + selling_plan all match. Every buy-box CTA appends its gift
       lines unconditionally, so clicking a CTA twice doubled every gift.
    3. The cart drawer's +/- buttons applied to gift lines like any other line.

  Patching each surface separately is whack-a-mole, and none of it survives a
  direct POST to /cart/change.js. So instead of trusting the UI, this
  reconciles the WHOLE cart after every cart mutation and immediately before
  every checkout hand-off.

  SAFETY PROPERTY
  The reconciler only ever REDUCES a gift line's quantity. It never raises one
  and never adds a line, so it cannot invent an entitlement the customer did
  not already have. A bug here can under-gift, never over-charge.

  LIMITS -- READ THIS BEFORE TRUSTING THE GUARD
  This is theme-side code. It stops the opportunistic behaviour we see in the
  browser -- clicking "+" in the cart, double-firing a CTA, or adding a gift
  variant on its own from /cart/add.js -- because all of that runs through the
  page and therefore through the fetch wrapper below.

  IT CANNOT STOP A CART PERMALINK. /cart/<variant>:<qty> is served by Shopify
  and redirects straight to /checkouts/..., so no theme JavaScript ever
  executes. Anyone with that URL gets whatever it names.

  SEP 2026 INCIDENT -- exactly that happened. The $0 creatine tub
  (variant 52947221250359) was a normal product published to the Online Store,
  so https://zenbodi.com/cart/52947221250359:3 put three free tubs into a live
  checkout; the cart totalled $0, which meant the only shipping rate offered was
  the $0 "Economy" rate, so the whole order came to $0.00. The link was shared
  and ~100 free orders landed. Listing the variant in giftVariantIds below closes
  the in-browser path, but it did NOT and could not close that one.

  THE REAL FIX IS IN ADMIN, NOT HERE. A $0 product published to the Online Store
  is orderable by anyone, full stop. Either the gift products stop being $0
  (give them their real price and zero them with an automatic BXGY discount that
  only fires when the qualifying item is present -- which is how "ZenBean Welcome
  Kit Free Gifts" already works), or checkout is gated by a Shopify Function
  (Cart & Checkout Validation). Unpublishing them is not an option on its own:
  /cart/add.js cannot add a product that is off the Online Store channel, so it
  would break every gift the theme adds.
*/
(function () {
  "use strict";

  if (window.ZenGiftGuard) return;

  /* Each family is one "offer". Entitlement is capped per family, so buying
     Kids Cafe AND ZenBean yields one gift set each, while a 3-pouch Kids Cafe
     bundle still yields exactly one Kids Cafe gift set. */
  var FAMILIES = [
    {
      key: "kids-cafe",
      label: "Kids Cafe welcome kit",
      // Any of these on a line means "this is a qualifying purchase".
      offerProps: ["_kids_cafe_welcome_kit", "_kids_cafe_pouch_index", "_kids_cafe_plan"],
      giftProps: ["_kids_cafe_welcome_gift"],
      // Hardcoded in kcpc-hero-buybox, kidscafe-v2-content and
      // zenbodi-home-v2-product-data. Kept here as a backstop so gift lines
      // added by an older cached script are still recognised.
      giftVariantIds: [52113525145911, 52113527570743, 52113527669047, 52113527701815]
    },
    {
      /* Kids Cafe offer B (Sep 2026): the Buy 2 Get 1 bundle auto-adds one free
         "Anti-Crash Focus Coffee" bag -- the $41 Zenbean Ground Bag.

         Its own family, not part of "kids-cafe", because the entitlement is
         different: every Kids Cafe plan earns the hero kit, but only the B bundle
         earns the coffee. The B bundle's pouch lines carry _kc_adult_coffee.

         giftVariantIds is deliberately EMPTY. That bag is a normal product a
         customer can buy for $41, and listing its ID here would let the guard trim
         a real purchase. Recognition is by our marker property only. */
      key: "kids-cafe-adult-coffee",
      label: "Kids Cafe free adult coffee",
      offerProps: ["_kc_adult_coffee"],
      giftProps: ["_kids_cafe_adult_coffee_gift"],
      giftVariantIds: [],
      /* The bundle is two pouches on two lines of one each, so the per-line
         offerQuantity rule cannot express "still buying two". This sums the marked
         lines instead: drop to one pouch in the cart and the coffee goes with it.

         Without this the customer keeps a $41 bag the Buy 2 discount no longer
         zeroes -- they would pay for a gift the page told them was free. */
      offerTotalQuantity: 2
    },
    {
      key: "zenbean",
      label: "ZenBean welcome kit",
      /* _zenbean_subscription: the Subscribe & Save coffee line (zb-offer-sub.js, Nov 2026). It
         does not say "welcome kit" because line properties are copied onto every renewal order. */
      offerProps: ["_zenbean_welcome_kit", "_zenbean_subscription"],
      giftProps: ["_zenbean_welcome_gift"],
      /* ZenBean gift variants are picked per-section in the theme editor, so
         this is not an exhaustive list -- recognition is still primarily
         property-based, including the legacy visible "Type" label below.
         These are the three $0 products actually wired up on the live store
         (Coffee Mug, Coffee Scoop, Memory + Focus Training Book), listed so a
         line added WITHOUT our properties -- i.e. straight from the product
         page or /cart/add.js -- is still recognised as a gift. They stay
         untrusted (see planCorrections), so such a line collapses to 1 rather
         than 0: a real customer's single gift is never deleted. */
      giftVariantIds: [52596994834743, 52596995457335, 52596996047159],
      legacyGiftLabel: "Free gift with your order"
    },
    {
      /* Creatine clearance page (page.zenbean-creatine). Unlike the welcome kit
         this offer gives MORE THAN ONE of a single free product, so its
         entitlement is a quantity rather than a presence flag.

         giftQuantity must match "Free creatine tubs" on the ZBPC Buy Box
         (creatine) section. If the section is raised to 4 and this is left at 3,
         the guard silently trims the fourth tub back off every cart. */
      key: "zenbean-creatine",
      label: "ZenBean creatine clearance",
      offerProps: ["_zenbean_creatine_offer"],
      giftProps: ["_zenbean_creatine_free"],
      /* Variant 52947221250359 is the $0 tub
         (creatine-monohydrate-unflavored-free-with-zenbean-3-pack).

         SEP 2026 INCIDENT: this was [] , so a tub added without our properties
         was not recognised as a gift at all and the guard left it alone. A bare
         POST to /cart/add.js -- and the product's own page -- yielded three free
         tubs with no coffee in the cart. Listing the ID closes that path. */
      giftVariantIds: [52947221250359],
      /* Unlike the welcome-kit gifts, this page has ONLY ever added its tubs
         with _zenbean_creatine_free (it launched 5 Sep 2026 with the markers
         already in place). So there is no such thing as a legitimate pre-marker
         creatine cart to protect, and a tub recognised by ID alone can be
         trusted the same as one carrying the marker -- meaning it collapses to
         0 when unearned, not to 1. Do NOT set this on the welcome-kit families,
         whose carts predate their markers. */
      strictGiftVariantIds: true,
      giftQuantity: 3,
      /* This offer is sold as a fixed bundle: "3 boxes + 3 creatine tubs" is one
         thing, not parts a customer can edit down. Without the lock, dropping
         the coffee line kept all three tubs -- a few dollars paid for ~$50 of
         creatine.

         offerQuantity is the minimum the offer line must still carry to have
         earned the tubs. It is 1 because the page now buys a single bundle
         variant priced at the whole bundle price; when the page instead buys
         three separate boxes this must be 3, or three tubs ride along with one
         box. Whoever changes "Bundle plan option value" on the ZBPC Buy Box
         (creatine) section changes which of those two shapes is in play. */
      offerQuantity: 1,
      /* Independent of the minimum: never render a stepper on this line. */
      lockOfferQuantity: true
    }
  ];

  var CART_MUTATION = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var INTERNAL_HEADER = "X-Zen-Gift-Guard";
  // Captured before the wrapper below replaces window.fetch, so the guard's own
  // requests never re-enter it.
  var nativeFetch = window.fetch.bind(window);

  function props(item) {
    return (item && item.properties) || {};
  }

  function hasAnyProp(item, keys) {
    var bag = props(item);
    for (var i = 0; i < keys.length; i++) {
      var value = bag[keys[i]];
      if (value !== undefined && value !== null && value !== "") return true;
    }
    return false;
  }

  function isGiftLine(family, item) {
    if (hasAnyProp(item, family.giftProps)) return true;
    if (family.legacyGiftLabel && props(item).Type === family.legacyGiftLabel) return true;
    var variantId = Number(item && item.variant_id);
    for (var i = 0; i < family.giftVariantIds.length; i++) {
      if (family.giftVariantIds[i] === variantId) return true;
    }
    return false;
  }

  /* Gift-first: a line that qualifies as a gift is never also counted as the
     purchase that earns a gift. Without this, a doubled gift line could
     bootstrap its own entitlement. */
  function isOfferLine(family, item) {
    if (isGiftLine(family, item)) return false;
    return hasAnyProp(item, family.offerProps);
  }

  /* "One gift set per offer": present-or-not, not per unit. Three pouches is
     still one welcome kit -- which is also how a single CTA click already
     behaves. A family may declare giftQuantity when its set is more than one of
     the same product (the creatine clearance ships three tubs); buying the offer
     twice still yields one set, so this can only under-gift, never over-gift.

     A family may also declare offerQuantity, meaning the offer line has to still
     carry at least that many units to have earned anything. Families without it
     use 1, which every real line satisfies, so their behaviour is unchanged. */
  function entitlementFor(family, items) {
    var required = family.offerQuantity || 1;

    /* Families whose offer is spread over several lines (one per pouch) count the
       total instead of asking any single line to carry the whole quantity. */
    if (family.offerTotalQuantity) {
      var total = 0;
      for (var t = 0; t < items.length; t++) {
        if (isOfferLine(family, items[t])) total += Number(items[t].quantity) || 0;
      }
      return total >= family.offerTotalQuantity ? family.giftQuantity || 1 : 0;
    }

    for (var i = 0; i < items.length; i++) {
      if (!isOfferLine(family, items[i])) continue;
      if ((Number(items[i].quantity) || 0) < required) continue;
      return family.giftQuantity || 1;
    }
    return 0;
  }

  /* The offer line of a bundled family. Its quantity is fixed, so the cart page
     and drawer render it without a stepper -- but it keeps its Remove control,
     because dropping the whole offer is a legitimate thing to want. */
  function isBundleOfferLine(family, item) {
    if (!family.lockOfferQuantity) return false;
    return isOfferLine(family, item);
  }

  function isGiftItem(item) {
    return FAMILIES.some(function (family) {
      return isGiftLine(family, item);
    });
  }

  function isBundleItem(item) {
    return FAMILIES.some(function (family) {
      return isBundleOfferLine(family, item);
    });
  }

  /* ------------------------------------------------------------------ *
     Cart requests. Every internal request carries INTERNAL_HEADER so the
     fetch wrapper below can tell our own corrections apart from a caller's
     mutation and not recurse.
   * ------------------------------------------------------------------ */
  function requestJson(url, options) {
    var config = options || {};
    config.headers = Object.assign({ Accept: "application/json" }, config.headers || {});
    config.headers[INTERNAL_HEADER] = "1";

    return nativeFetch(url, config).then(function (response) {
      return response.json().then(
        function (json) {
          if (!response.ok) throw new Error((json && json.description) || "Cart request failed");
          return json;
        },
        function () {
          if (!response.ok) throw new Error("Cart request failed");
          return null;
        }
      );
    });
  }

  function fetchCart() {
    return requestJson("/cart.js");
  }

  function changeLine(lineKey, quantity) {
    return requestJson("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: Math.max(0, Number(quantity) || 0) })
    });
  }

  /* Returns the corrections a cart needs, without applying them. Exported so
     the cart page can reason about the same rule when rendering. */
  function planCorrections(cart) {
    var items = (cart && cart.items) || [];
    var corrections = [];

    FAMILIES.forEach(function (family) {
      var allowance = entitlementFor(family, items);

      items.forEach(function (item) {
        if (!isGiftLine(family, item)) return;
        var quantity = Number(item.quantity) || 0;

        /* A gift carrying one of the underscore markers came from current
           theme code, so its family's offer markers are trustworthy and we can
           enforce the full allowance -- including dropping to 0 when the offer
           has been removed from the cart.

           A gift recognised only by a legacy signal (the visible "Type" label,
           or a known gift variant ID) may predate those markers. For those we
           cannot tell "offer was removed" from "cart made before the markers
           shipped", and silently deleting a real customer's gift is worse than
           leaving one behind -- so we only collapse duplicates down to one. */
        var trusted = hasAnyProp(item, family.giftProps) || Boolean(family.strictGiftVariantIds);
        var cap = trusted ? allowance : Math.max(allowance, 1);

        if (quantity <= cap) return;
        corrections.push({
          family: family.key,
          key: item.key,
          title: item.product_title || item.title || "",
          from: quantity,
          to: cap,
          trusted: trusted
        });
      });
    });

    return corrections;
  }

  /* Applies corrections one at a time. Line keys survive /cart/change.js, so
     each correction is safe to issue against the keys planned up front. */
  function applyCorrections(cart, corrections) {
    var latest = cart;

    return corrections
      .reduce(function (chain, correction) {
        return chain.then(function () {
          return changeLine(correction.key, correction.to).then(function (updated) {
            if (updated) latest = updated;
          });
        });
      }, Promise.resolve())
      .then(function () {
        return latest;
      });
  }

  function announce(cart, corrections) {
    if (!corrections.length || !cart) return;
    try {
      document.dispatchEvent(
        new CustomEvent("zen:gift-guard:corrected", { detail: { cart: cart, corrections: corrections } })
      );
      document.dispatchEvent(new CustomEvent("zen:cart:updated", { detail: { cart: cart } }));
      var theme = window.ZenTheme;
      if (theme && typeof theme.updateCartCount === "function") theme.updateCartCount(cart.item_count);
      if (theme && typeof theme.renderCartDrawer === "function") theme.renderCartDrawer(cart);
    } catch (error) {
      /* Announcing is best-effort -- the cart itself is already correct. */
    }
  }

  function runPass() {
    return fetchCart()
      .then(function (cart) {
        var corrections = planCorrections(cart);
        if (!corrections.length) return cart;
        return applyCorrections(cart, corrections).then(function (finalCart) {
          announce(finalCart, corrections);
          return finalCart;
        });
      })
      .catch(function (error) {
        /* Never block the customer on a guard failure. Worst case we are back
           to today's behaviour, which the Admin-side discount cap must cover. */
        if (window.console && console.warn) console.warn("[zen-gift-guard] reconcile failed:", error);
        return null;
      });
  }

  /* Passes run strictly one after another. Sharing an in-flight pass would be
     wrong: a pass started before a mutation planned its corrections off the
     pre-mutation cart, so a caller joining it would be told "all clear" about
     a cart that pass never saw. */
  var queue = Promise.resolve();

  function reconcile() {
    var pass = queue.then(runPass, runPass);
    queue = pass.catch(function () {});
    return pass;
  }

  /* ------------------------------------------------------------------ *
     Interception. Every cart mutation in this theme goes through fetch, so
     wrapping it covers the six existing add-to-cart paths without touching
     them, plus any path added later.

     The wrapper AWAITS reconciliation before resolving. That matters: the
     buy-box CTAs do `.then(() => location.assign('/checkout'))`, so a
     background reconcile would race the redirect.
   * ------------------------------------------------------------------ */
  function isInternal(input, init) {
    var headers = (init && init.headers) || (input && input.headers);
    if (!headers) return false;
    if (typeof headers.get === "function") return Boolean(headers.get(INTERNAL_HEADER));
    return Boolean(headers[INTERNAL_HEADER]);
  }

  window.fetch = function (input, init) {
    var url = "";
    try {
      url = typeof input === "string" ? input : (input && input.url) || "";
    } catch (error) {
      url = "";
    }

    var promise = nativeFetch(input, init);
    if (isInternal(input, init) || !CART_MUTATION.test(url)) return promise;

    return promise.then(function (response) {
      if (!response || !response.ok) return response;
      // The response body is left untouched, so the caller still reads it.
      return reconcile().then(
        function () {
          return response;
        },
        function () {
          return response;
        }
      );
    });
  };

  /* ------------------------------------------------------------------ *
     Checkout gate. Last line of defence for carts inflated before this
     script shipped, or in a tab that has been open a while.
   * ------------------------------------------------------------------ */
  var CHECKOUT_TRIGGER = 'a[href*="/checkout"], [name="checkout"], [data-zen-cart-checkout], [data-zen-checkout]';

  var gating = false;

  function gateCheckout(trigger, event) {
    if (!trigger || trigger.hasAttribute("data-zen-gift-guard-cleared")) return;

    // Swallow impatient double-clicks rather than starting a second pass.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (gating) return;
    gating = true;

    var isButton = trigger.tagName === "BUTTON";
    var label = trigger.textContent;
    if (isButton) {
      trigger.disabled = true;
      trigger.textContent = "Checking out…";
    }

    reconcile().then(function () {
      gating = false;
      if (isButton) {
        trigger.disabled = false;
        trigger.textContent = label;
      }
      /* Replay the real click instead of navigating to a hardcoded /checkout.
         That preserves whatever the element actually does -- an <a> keeps its
         full href including ?discount=, a name="checkout" button still POSTs
         the cart form -- and lets analytics listeners fire normally. The flag
         makes this second click pass straight through the gate. */
      trigger.setAttribute("data-zen-gift-guard-cleared", "true");
      trigger.click();
    });
  }

  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      gateCheckout(target.closest(CHECKOUT_TRIGGER), event);
    },
    true
  );

  document.addEventListener(
    "submit",
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== "checkout") return;
      gateCheckout(submitter, event);
    },
    true
  );

  window.ZenGiftGuard = {
    reconcile: reconcile,
    planCorrections: planCorrections,
    isGiftItem: isGiftItem,
    isBundleItem: isBundleItem,
    fetchCart: fetchCart,
    families: FAMILIES
  };

  /* Sweep once on load so carts already inflated by the old behaviour are
     corrected the next time the customer visits any page. */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcile);
  } else {
    reconcile();
  }
})();
